import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { ShieldCheck, Rocket, AlertTriangle, BarChart3, ScanLine } from "lucide-react";

/* ---------- Count-up number ---------- */
export const CountUp = ({ to, prefix = "", suffix = "", duration = 1.6, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setValue(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {value.toLocaleString()}
      {suffix}
    </span>
  );
};

const visualFrame =
  "relative w-full h-full min-h-[280px] rounded-2xl bg-[#0d0e14] border border-border overflow-hidden p-5 flex flex-col";

/* ---------- Google SEO: ranking climb line chart ---------- */
export const SeoRankVisual = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <div ref={ref} className={visualFrame} data-testid="visual-seo">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-widest text-muted-foreground">Google Ranking · 6 Months</span>
        <motion.span
          initial={{ opacity: 0, scale: 0.7 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ delay: 1.6, duration: 0.5 }}
          className="text-xs font-semibold bg-primary/15 text-primary border border-primary/30 rounded-full px-3 py-1"
        >
          #1 on Google
        </motion.span>
      </div>
      <svg viewBox="0 0 400 210" className="w-full flex-1" preserveAspectRatio="none">
        <defs>
          <linearGradient id="seoLine" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#2AA7A1" />
            <stop offset="100%" stopColor="#D6B36A" />
          </linearGradient>
          <linearGradient id="seoFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#D6B36A" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#D6B36A" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[42, 84, 126, 168].map((y) => (
          <line key={y} x1="0" y1={y} x2="400" y2={y} stroke="#23253a" strokeWidth="1" />
        ))}
        <motion.path
          d="M8,190 C60,185 80,170 120,150 C160,130 180,120 220,95 C260,70 290,55 330,35 C355,23 375,18 392,14"
          fill="none"
          stroke="url(#seoLine)"
          strokeWidth="3.5"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={inView ? { pathLength: 1 } : {}}
          transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
        />
        <motion.path
          d="M8,190 C60,185 80,170 120,150 C160,130 180,120 220,95 C260,70 290,55 330,35 C355,23 375,18 392,14 L392,210 L8,210 Z"
          fill="url(#seoFill)"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 1.2, duration: 0.8 }}
        />
        <motion.circle
          cx="392"
          cy="14"
          r="6"
          fill="#D6B36A"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: [0, 1, 0.5, 1] } : {}}
          transition={{ delay: 1.7, duration: 1.4, repeat: Infinity, repeatDelay: 0.5 }}
        />
      </svg>
      <div className="flex justify-between text-[10px] font-mono-meta text-muted-foreground mt-2">
        <span>Page 5</span>
        <span>Page 3</span>
        <span>Page 2</span>
        <span className="text-primary">Position #1</span>
      </div>
    </div>
  );
};

/* ---------- Digital Marketing: animated funnel ---------- */
export const FunnelVisual = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const stages = [
    { label: "Ad Impressions", value: 120000, width: "100%", color: "bg-accent/50" },
    { label: "Website Clicks", value: 8400, width: "72%", color: "bg-accent/70" },
    { label: "Qualified Leads", value: 1260, width: "46%", color: "bg-primary/60" },
    { label: "Paying Customers", value: 312, width: "26%", color: "bg-primary" },
  ];
  return (
    <div ref={ref} className={visualFrame} data-testid="visual-marketing">
      <span className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
        Campaign Funnel · Monthly
      </span>
      <div className="flex-1 flex flex-col justify-center gap-3.5">
        {stages.map((stage, i) => (
          <div key={stage.label}>
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="text-muted-foreground">{stage.label}</span>
              <span className="font-mono-meta text-foreground">
                <CountUp to={stage.value} duration={1.4 + i * 0.2} />
              </span>
            </div>
            <div className="h-6 rounded-lg bg-secondary/60 overflow-hidden">
              <motion.div
                className={`h-full rounded-lg ${stage.color}`}
                initial={{ width: 0 }}
                animate={inView ? { width: stage.width } : {}}
                transition={{ duration: 1.1, delay: i * 0.18, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground mt-4 font-mono-meta">
        Tracked end-to-end · optimised weekly · reported monthly
      </p>
    </div>
  );
};

/* ---------- E-commerce: platform bars ---------- */
export const CommerceBarsVisual = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const platforms = [
    { label: "Shopify", height: 62, growth: "+38%" },
    { label: "Amazon", height: 92, growth: "+64%" },
    { label: "eBay", height: 48, growth: "+27%" },
    { label: "Etsy", height: 40, growth: "+22%" },
  ];
  return (
    <div ref={ref} className={visualFrame} data-testid="visual-commerce">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-widest text-muted-foreground">
          Revenue by Channel
        </span>
        <BarChart3 className="w-4 h-4 text-primary" />
      </div>
      <div className="flex-1 flex items-end justify-around gap-4 pb-2 pt-6">
        {platforms.map((p, i) => (
          <div key={p.label} className="flex flex-col items-center gap-2 flex-1 h-full justify-end">
            <motion.span
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ delay: 0.9 + i * 0.15 }}
              className="text-[11px] font-mono-meta text-primary"
            >
              {p.growth}
            </motion.span>
            <motion.div
              className={`w-full max-w-[52px] rounded-t-lg ${i === 1 ? "bg-primary" : "bg-accent/70"}`}
              initial={{ height: 0 }}
              animate={inView ? { height: `${p.height}%` } : {}}
              transition={{ duration: 1, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
            />
            <span className="text-[11px] text-muted-foreground">{p.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------- Custom system: live stock dashboard ---------- */
export const StockDashboardVisual = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const bars = [58, 76, 44, 88, 64, 92, 70];
  return (
    <div ref={ref} className={visualFrame} data-testid="visual-system">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs uppercase tracking-widest text-muted-foreground">
          Warehouse Dashboard · Live
        </span>
        <motion.span
          animate={{ opacity: [1, 0.3, 1] }}
          transition={{ duration: 1.6, repeat: Infinity }}
          className="flex items-center gap-1.5 text-[11px] text-emerald-400 font-mono-meta"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> LIVE
        </motion.span>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="rounded-xl bg-secondary/60 border border-border p-3.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Items in Stock</p>
          <p className="text-xl font-semibold text-foreground mt-1">
            <CountUp to={12480} />
          </p>
        </div>
        <div className="rounded-xl bg-secondary/60 border border-border p-3.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Scans Today</p>
          <p className="text-xl font-semibold text-primary mt-1">
            <CountUp to={1842} />
          </p>
        </div>
      </div>
      <div className="flex-1 flex items-end gap-2 min-h-[70px]">
        {bars.map((h, i) => (
          <motion.div
            key={i}
            className="flex-1 rounded-t-md bg-accent/60"
            initial={{ height: 0 }}
            animate={inView ? { height: `${h}%` } : {}}
            transition={{ duration: 0.9, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
          />
        ))}
      </div>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 1.4 }}
        className="mt-4 flex items-center gap-2 text-[11px] text-amber-400/90 bg-amber-400/10 border border-amber-400/20 rounded-lg px-3 py-2"
      >
        <AlertTriangle className="w-3.5 h-3.5" /> Low stock alert · SKU-2841 · auto-reorder triggered
      </motion.div>
    </div>
  );
};

/* ---------- Cyber security: pulsing shield ---------- */
export const ShieldVisual = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <div ref={ref} className={visualFrame} data-testid="visual-security">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-widest text-muted-foreground">
          Security Monitor · 24/7
        </span>
        <ScanLine className="w-4 h-4 text-accent-foreground/70" />
      </div>
      <div className="flex-1 relative flex items-center justify-center min-h-[170px]">
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            className="absolute rounded-full border border-primary/30"
            style={{ width: 90 + i * 60, height: 90 + i * 60 }}
            animate={{ scale: [1, 1.25], opacity: [0.5, 0] }}
            transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.8, ease: "easeOut" }}
          />
        ))}
        <motion.div
          initial={{ scale: 0.7, opacity: 0 }}
          animate={inView ? { scale: 1, opacity: 1 } : {}}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 w-20 h-20 rounded-2xl bg-primary/15 border border-primary/40 grid place-items-center"
        >
          <ShieldCheck className="w-9 h-9 text-primary" />
        </motion.div>
      </div>
      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="rounded-xl bg-secondary/60 border border-border py-2.5">
          <p className="text-base font-semibold text-foreground">
            <CountUp to={1284} />
          </p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Threats Blocked</p>
        </div>
        <div className="rounded-xl bg-secondary/60 border border-border py-2.5">
          <p className="text-base font-semibold text-primary">0</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Breaches</p>
        </div>
        <div className="rounded-xl bg-secondary/60 border border-border py-2.5">
          <p className="text-base font-semibold text-foreground">99.9%</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Uptime</p>
        </div>
      </div>
    </div>
  );
};

/* ---------- Website in 7 days timeline ---------- */
export const TimelineWeekVisual = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const days = [
    { day: "Day 1", label: "Brief & Design" },
    { day: "Day 2", label: "Design Approved" },
    { day: "Day 3", label: "Build Begins" },
    { day: "Day 4", label: "Features & Content" },
    { day: "Day 5", label: "Database & SSL" },
    { day: "Day 6", label: "Testing & Polish" },
    { day: "Day 7", label: "LIVE TO PUBLIC" },
  ];
  return (
    <div ref={ref} className={visualFrame} data-testid="visual-website">
      <div className="flex items-center justify-between mb-5">
        <span className="text-xs uppercase tracking-widest text-muted-foreground">
          Launch Timeline · 7 Days Max
        </span>
        <Rocket className="w-4 h-4 text-primary" />
      </div>
      <div className="flex-1 flex flex-col justify-center">
        <div className="relative h-2 rounded-full bg-secondary/70 mb-7">
          <motion.div
            className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-accent to-primary"
            initial={{ width: 0 }}
            animate={inView ? { width: "100%" } : {}}
            transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((d, i) => (
            <motion.div
              key={d.day}
              initial={{ opacity: 0, y: 10 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.25 + i * 0.3, duration: 0.45 }}
              className="flex flex-col items-center text-center gap-1.5"
            >
              <span
                className={`w-3.5 h-3.5 rounded-full border-2 ${
                  i === 6
                    ? "bg-primary border-primary shadow-[0_0_14px_rgba(214,179,106,0.8)]"
                    : "bg-accent/60 border-accent"
                }`}
              />
              <span className="text-[10px] font-mono-meta text-muted-foreground">{d.day}</span>
              <span
                className={`text-[9px] sm:text-[10px] leading-tight ${
                  i === 6 ? "text-primary font-semibold" : "text-muted-foreground/80"
                }`}
              >
                {d.label}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mt-5 font-mono-meta text-center">
        Domain · Hosting · SSL · Database — all included in the price
      </p>
    </div>
  );
};

/* ---------- Infrastructure: network nodes ---------- */
export const NetworkVisual = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const nodes = [
    { cx: 200, cy: 105, r: 17, main: true },
    { cx: 70, cy: 40, r: 10 },
    { cx: 330, cy: 45, r: 10 },
    { cx: 45, cy: 165, r: 10 },
    { cx: 345, cy: 170, r: 10 },
    { cx: 200, cy: 22, r: 10 },
    { cx: 130, cy: 195, r: 10 },
    { cx: 275, cy: 195, r: 10 },
  ];
  return (
    <div ref={ref} className={visualFrame} data-testid="visual-network">
      <span className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
        Infrastructure Map · Connected
      </span>
      <svg viewBox="0 0 400 220" className="w-full flex-1">
        {nodes.slice(1).map((n, i) => (
          <motion.line
            key={i}
            x1="200"
            y1="105"
            x2={n.cx}
            y2={n.cy}
            stroke="#2AA7A1"
            strokeWidth="1.4"
            strokeDasharray="5 5"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={inView ? { pathLength: 1, opacity: 0.55 } : {}}
            transition={{ duration: 0.8, delay: 0.15 * i }}
          />
        ))}
        {nodes.map((n, i) => (
          <motion.circle
            key={i}
            cx={n.cx}
            cy={n.cy}
            r={n.r}
            fill={n.main ? "#D6B36A" : "#171821"}
            stroke={n.main ? "#D6B36A" : "#2AA7A1"}
            strokeWidth="2"
            initial={{ scale: 0, opacity: 0 }}
            animate={inView ? { scale: 1, opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.1 * i, ease: [0.22, 1, 0.36, 1] }}
            style={{ transformOrigin: `${n.cx}px ${n.cy}px` }}
          />
        ))}
        <motion.circle
          cx="200"
          cy="105"
          r="26"
          fill="none"
          stroke="#D6B36A"
          strokeWidth="1.5"
          animate={{ scale: [1, 1.5], opacity: [0.7, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
          style={{ transformOrigin: "200px 105px" }}
        />
      </svg>
      <div className="flex justify-between text-[11px] font-mono-meta text-muted-foreground mt-2">
        <span>Servers · Network · Database</span>
        <span className="text-emerald-400">All systems operational</span>
      </div>
    </div>
  );
};
